import { Button } from '@mui/material';
import { useBoringVaultV1 } from 'boring-vault-ui';
import { BoringQueueStatus } from 'boring-vault-ui/types';
import { getValidChain, OLstToken } from 'modules/api';
import { useConnection } from 'modules/auth';
import { getErrorMessage } from 'modules/common/utils/getErrorMessage';
import { useTranslation } from 'modules/i18n';
import { showNotification } from 'modules/notifications';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { translation } from './translation';
import { useIsWithdrawalInProgress } from './useIsWithdrawalInProgress';
import { usePendingWithdrawal } from './usePendingWithdrawal';
import { useRefetchWithdrawalStatus } from './useRefetchWithdrawalStatus';
import { BTC_DECIMALS } from 'modules/common/const';
import { getLbtcAddressConfig, OEnv } from '@lombard.finance/sdk';
import { useVaultProvider } from 'modules/dashboard/components/Deposit/VaultContextProvider/hooks/useVaultProvider';
import { dashboardRouteConfig } from 'modules/dashboard';

const lbtcConfig = getLbtcAddressConfig(OEnv.prod);

interface IUseCancelWithdrawalResult {
  cancelWithdraw: () => Promise<void>;
  isLoading: boolean;
}

interface IUseCancelWithdrawalProps {
  onSuccess: () => void;
}

export const useCancelWithdrawal = ({
  onSuccess,
}: IUseCancelWithdrawalProps): IUseCancelWithdrawalResult => {
  const { address, chainId } = useConnection();
  const [isLoading, setIsLoading] = useState(false);
  const { provider } = useVaultProvider();
  const { withdrawQueueStatuses, boringQueueStatuses, boringQueueCancel } =
    useBoringVaultV1();
  const [statuses, setStatuses] = useState<Record<string, BoringQueueStatus[]>>(
    {},
  );
  const [refetchCount, setRefetchCount] = useState(0);
  const { t, keys } = useTranslation(translation);
  const navigate = useNavigate();
  const { isWithdrawalInProgress } = useIsWithdrawalInProgress();
  const { clearStatuses } = usePendingWithdrawal();
  const refetchWithdrawalStatus = useRefetchWithdrawalStatus(
    address,
    getValidChain(chainId),
  );
  const refChainId = useRef(chainId);

  const cancelWithdraw = useCallback(async () => {
    setIsLoading(true);
    if (!provider || !statuses || !address) {
      setIsLoading(false);
      return;
    }

    try {
      const [withdrawTransaction] = statuses[address];

      const signer = await provider.getSigner();

      const tokenOut = withdrawTransaction.tokenOut || {
        address: lbtcConfig[chainId as keyof typeof lbtcConfig],
        decimals: BTC_DECIMALS,
        displayName: OLstToken.LBTC,
      };

      await boringQueueCancel(signer, tokenOut);
      setRefetchCount(value => value + 1);
    } catch (error) {
      showNotification({
        message: getErrorMessage(error),
        variant: 'error',
      });
      setIsLoading(false);
    }
  }, [provider, statuses, boringQueueCancel, chainId]);

  /**
   * Clear statuses and refetch data after successful withdrawal cancellation
   */
  const onCancelSuccess = () => {
    if (isWithdrawalInProgress) {
      refetchWithdrawalStatus();
    }
    clearStatuses();
    setStatuses({});
  };

  useEffect(() => {
    const fetchStatuses = async () => {
      setIsLoading(true);

      if (!provider || !address) {
        setIsLoading(false);
        return;
      }

      try {
        const signer = await provider.getSigner();
        const fetchedStatuses: BoringQueueStatus[] = await boringQueueStatuses(
          signer!,
        );

        setIsLoading(false);
        setStatuses(state => ({ ...state, [address]: fetchedStatuses }));
        const [withdrawTransaction] = fetchedStatuses;

        if (!withdrawTransaction) {
          showNotification({
            header: t(keys.withdrawalCancelledTitle),
            message: t(keys.withdrawalCancelledBody),
            action: (
              <Button
                component={Link}
                to={dashboardRouteConfig.main.generatePath()}
                variant="contained"
                color="secondary"
              >
                {t(keys.depositAgain)}
              </Button>
            ),
            variant: 'success',
          });
          navigate(dashboardRouteConfig.main.generatePath());
          onCancelSuccess();
          onSuccess?.();
        }
      } catch (error) {
        showNotification({
          message: getErrorMessage(error),
          variant: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };

    const timeout = setTimeout(() => {
      fetchStatuses();
    }, 500);

    if (chainId !== refChainId.current) {
      clearTimeout(timeout);
    }

    return () => clearTimeout(timeout);
  }, [provider, withdrawQueueStatuses, refetchCount, chainId]);

  return { cancelWithdraw, isLoading };
};
